import * as React from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Head from 'next/head';

import styles from './EssentialPages.module.css';
import { ReduxState } from 'ducks';
import { LinkableTextArea } from 'components/LinkableTextArea/LinkableTextArea';

export const Covid19 = (): any => {
  const { t } = useTranslation();
  const settings = useSelector((state: ReduxState) => state.server.settings.all);

  const covid19Settings = settings?.essential_pages?.covid19_settings;

  return (
    <>
      <Head>
        <title>{t('COVID-19 Guidelines')}</title>

        <meta property="og:title" content={t('COVID-19 Guidelines')} />

        {covid19Settings?.introduction && (
          <meta property="og:description" content={covid19Settings.introduction} />
        )}
      </Head>
      <div className={clsx(styles['base-wrap'], styles['color-txt'], styles['color-bg'])}>
        <main className={clsx(styles['base-main styles.paddingTop32-sp'])}>
          <div className={clsx(styles['base-info'])}>
            <h1 className={clsx(styles['base-info__headline'])}>{t('COVID-19 Guidelines')}</h1>

            <section className={clsx(styles['base-info__frame'])}>
              {covid19Settings?.introduction && (
                <div className={clsx(styles['base-info__frame__maintxt'], 'newline')}>
                  <LinkableTextArea>{covid19Settings?.introduction}</LinkableTextArea>
                </div>
              )}

              <div className={clsx(styles['base-info__frame__listBox'])}>
                {((covid19Settings?.precautions || []).length > 0 ||
                  (covid19Settings?.custom_precautions || []).length > 0) && (
                  <div className={clsx(styles['base-info__frame__listBox__box'])}>
                    <div
                      className={clsx(
                        styles['base-info__frame__listBox__box__ttl'],
                        'maincolor-txt'
                      )}
                    >
                      <p>
                        <div className={clsx('maincolor-ic')}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="32"
                            height="32"
                            viewBox="0 0 32 32"
                          >
                            <rect width="32" height="32" fill="none" />
                            <g>
                              <path
                                d="M22,1.57,19.88,3.64l-.94-1a.83.83,0,0,0-1.1,0c-8.76,7.41-8.21,6.93-8.27,7L2.65,17.84a.83.83,0,0,0,0,1.1l1,.94L1.57,22a1.85,1.85,0,0,0,0,2.61l5.86,5.86a1.85,1.85,0,0,0,2.61,0l2.08-2.07.94.95a.83.83,0,0,0,1.1,0c8.76-7.41,8.21-6.93,8.27-7l6.92-8.18a.83.83,0,0,0,0-1.1l-.95-.94L30.43,10a1.85,1.85,0,0,0,0-2.61L24.57,1.57A1.85,1.85,0,0,0,22,1.57ZM8.83,29.22a.14.14,0,0,1-.19,0L2.78,23.36a.14.14,0,0,1,0-.19l2.07-2.08,6.06,6.06ZM23.88,14.13c-4.58,5.94-4.1,5.39-9.75,9.75a.77.77,0,0,1-.49.17.81.81,0,0,1-.5-1.45c5.8-4.47,5.2-3.93,9.46-9.46A.82.82,0,0,1,23.74,13,.81.81,0,0,1,23.88,14.13Zm-2.74-2.74c-4.61,5.95-4.09,5.37-9.75,9.75a.84.84,0,0,1-.5.17.82.82,0,0,1-.5-1.46c5.84-4.51,5.19-3.93,9.46-9.46A.82.82,0,0,1,21,10.25.81.81,0,0,1,21.14,11.39ZM18.39,8.65c-4.4,5.65-3.8,5.12-9.74,9.74a.81.81,0,0,1-1-1.28c5.81-4.51,5.14-3.9,9.46-9.46a.81.81,0,0,1,1.28,1Zm10.83,0a.14.14,0,0,1,0,.19l-2.07,2.08L21.09,4.85l2.08-2.07a.14.14,0,0,1,.19,0Z"
                                fill="#fff"
                              />
                            </g>
                          </svg>
                        </div>
                      </p>
                      <h2>{t('Safety measures')}</h2>
                    </div>
                    <ul className={clsx(styles['base-info__frame__listBox__box__list'])}>
                      {(covid19Settings?.precautions || []).map((precaution) => {
                        return <li key={precaution}>{precaution}</li>;
                      })}
                      {(covid19Settings?.custom_precautions || []).map((precaution) => {
                        return <li key={precaution}>{precaution}</li>;
                      })}
                    </ul>
                  </div>
                )}

                {((covid19Settings?.to_guests || []).length > 0 ||
                  (covid19Settings?.custom_to_guests || []).length > 0) && (
                  <div className={clsx(styles['base-info__frame__listBox__box'])}>
                    <div
                      className={clsx(
                        styles['base-info__frame__listBox__box__ttl'],
                        'maincolor-txt'
                      )}
                    >
                      <p>
                        <div className={clsx('maincolor-ic')}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="32"
                            height="32"
                            viewBox="0 0 32 32"
                          >
                            <rect width="32" height="32" fill="none" />
                            <g>
                              <path
                                d="M16,2A14,14,0,1,0,30,16,14,14,0,0,0,16,2Zm.29,21.79c-.11.11-.09.1-.09.1a.23.23,0,0,1-.2.1h0a.23.23,0,0,1-.2-.1s0,0-.09-.1C12.58,20.77,8,18.9,8,14.85,8,11.69,9.86,10,12.11,10A3.86,3.86,0,0,1,16,13.35,3.86,3.86,0,0,1,19.89,10C22.14,10,24,11.69,24,14.85,24,18.9,19.42,20.77,16.29,23.79Z"
                                fill="#fff"
                              />
                            </g>
                          </svg>
                        </div>
                      </p>
                      <h2>{t('To guests')}</h2>
                    </div>
                    <ul className={clsx(styles['base-info__frame__listBox__box__list'])}>
                      {(covid19Settings?.to_guests || []).map((toGuest) => {
                        return <li key={toGuest}>{toGuest}</li>;
                      })}
                      {(covid19Settings?.custom_to_guests || []).map((toGuest) => {
                        return <li key={toGuest}>{toGuest}</li>;
                      })}
                    </ul>
                  </div>
                )}
              </div>

              {covid19Settings?.other_message && (
                <div className={clsx(styles['base-info__frame__txt'], 'newline')}>
                  <LinkableTextArea>{covid19Settings?.other_message}</LinkableTextArea>
                </div>
              )}
            </section>
          </div>
        </main>
      </div>
    </>
  );
};
